import RequestMedia from "@/components/trip/RequestMedia";
/**
 * IncomingTripListener
 *
 * Global driver-side listener for shared-service requests (ride / motor /
 * delivery / runner / laundry / trash). Polls `/api/rideshare/bookings`
 * while the driver is approved + online, and shows every live request
 * (or customer counter-back) in a persistent slide-up that only closes
 * once the pro accepts, declines or counters — or the window expires.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { acceptLabel } from "@/lib/cancelReasons";
import { MapPin, Car, Bike, Package, User, Check, MessageSquare, Loader2, Mic, Play, Pause, CalendarClock, X } from "lucide-react";
import { LaundryIcon as WashingMachine, TrashBinIcon as Trash2 } from "@/components/ServiceIcons";
import { API, useAuth } from "@/App";
import CircularCountdown from "@/components/CircularCountdown";
import PersistentSheet from "@/components/requests/PersistentSheet";
import { startRinging } from "@/lib/chime";
import { resolveMediaUrl } from "@/lib/mediaUrl";
import { tripFare, tripEstimate, isRiderOffer } from "@/utils/tripFare";
import { offerWindow, windowLive, notifyBookingUpdated } from "@/lib/requestWindow";

const POLL_MS = 8000;
const INCOMING_STATUSES = new Set(["negotiating", "pending"]);

const isVanTrip = (t) => t?.rideshare_type === "ride" && !!(t.van_note || t.van_voice_note || t.van_scheduled_start || t.van_scheduled_end);

// Newest offer on MY thread (targeted at my artisan id, or untargeted).
const myLatestOffer = (trip, aid) => {
  const offers = Array.isArray(trip?.offers) ? trip.offers : [];
  return [...offers].reverse().find((o) => o && (!o.driver_id || !aid || o.driver_id === aid)) || null;
};

// What this pro can act on right now: the customer's open offer, or a plain request with no thread yet.
// Returns undefined when the ball is in the customer's court (my counter open) or I already answered.
const actionable = (trip, aid) => {
  const latest = myLatestOffer(trip, aid);
  if (!latest) return { trip, offer: null };
  if (latest.status !== "open" || latest.by === "driver") return undefined;
  return { trip, offer: latest };
};

const tripWindow = (trip, offer) => offerWindow(
  offer,
  trip.updated_at || trip.created_at,
  trip.rideshare_type === "runner" ? 240 : (isVanTrip(trip) ? 180 : 90),
);

// Changes whenever the customer counters (amount / counter_count / fresh window).
const tripSig = (trip, offer) => offer
  ? `${trip.id}|${offer.id}|${offer.amount}|${offer.counter_count || 0}|${offer.created_at || ""}`
  : `${trip.id}|${trip.status}|${trip.artisan_user_id || ""}`;

export default function IncomingTripListener({ demo }) {
  const { token, user } = useAuth();
  const [queue, setQueue] = useState(() => (demo ? [{ trip: demo, offer: demo.demo_offer || null, sig: "demo" }] : [])); // [{ trip, offer, sig }]
  const [gone, setGone] = useState(null); // why the open request is no longer available
  const [busy, setBusy] = useState(false);
  const [counterOpen, setCounterOpen] = useState(!!demo?.demo_counter_open);
  const [counterAmount, setCounterAmount] = useState(demo?.demo_counter_amount || "");
  const [voicePlaying, setVoicePlaying] = useState(false);
  const seenRef = useRef(new Set());
  const aidRef = useRef(null);
  const voiceAudioRef = useRef(null);
  const activeRef = useRef(false);

  const current = queue[0] || null;
  const incoming = current?.trip || null;
  const incomingId = incoming?.id;
  const incomingSig = current?.sig;
  const win = incoming ? tripWindow(incoming, current.offer) : null;

  // Ring (chime + vibrate every 3 s) until the pro acts, for at most 90 s of the window.
  useEffect(() => {
    if (!incomingSig || gone || !win || demo) return undefined;
    const t0 = Date.parse(win.startedAt);
    const base = Number.isFinite(t0) ? Math.min(t0, Date.now()) : Date.now();
    return startRinging({ untilMs: base + Math.min(90, win.totalSec) * 1000 });
  }, [incomingSig, gone]); // eslint-disable-line react-hooks/exhaustive-deps

  const pollTrips = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/rideshare/bookings?token=${token}`);
      const trips = (res.data || []).filter((t) => INCOMING_STATUSES.has(t.status));
      const live = trips
        .map((trip) => actionable(trip, aidRef.current))
        .filter(Boolean)
        .map((it) => ({ ...it, sig: tripSig(it.trip, it.offer) }))
        .filter((it) => windowLive(tripWindow(it.trip, it.offer)));
      setQueue((prev) => {
        const byId = new Map(live.map((it) => [it.trip.id, it]));
        // Refresh queued trips (a customer counter-back swaps the offer in place); drop waiting ones that went away.
        const kept = prev.filter((it, i) => i === 0 || byId.has(it.trip.id)).map((it) => byId.get(it.trip.id) || it);
        const ids = new Set(kept.map((it) => it.trip.id));
        const fresh = live.filter((it) => !ids.has(it.trip.id) && !seenRef.current.has(it.sig));
        [...kept, ...fresh].forEach((it) => seenRef.current.add(it.sig));
        return fresh.length || kept.some((it, i) => it !== prev[i]) ? [...kept, ...fresh] : prev;
      });
    } catch (_) { /* silent — a transient network blip shouldn't spam */ }
  }, [token]);

  // Instant re-poll when a push / socket event about a request lands.
  useEffect(() => {
    if (!token || user?.role !== "artisan") return undefined;
    const onBookingUpdated = (ev) => {
      const t = String(ev?.detail?.type || "");
      if (t.startsWith("rideshare_") || t.startsWith("offer_") || t === "new_booking") pollTrips();
    };
    window.addEventListener("yorm:booking-updated", onBookingUpdated);
    return () => window.removeEventListener("yorm:booking-updated", onBookingUpdated);
  }, [token, user?.role, pollTrips]);

  // Gate polling on approved + online; re-checked every 20 s.
  useEffect(() => {
    if (demo) return undefined;
    activeRef.current = false;
    seenRef.current = new Set();
    setQueue([]);
    if (!token || user?.role !== "artisan") return undefined;
    let cancelled = false;
    let intervalId = null;
    const checkAndStart = async () => {
      try {
        const res = await axios.get(`${API}/rideshare/me?token=${token}`);
        const info = res.data || {};
        if (cancelled) return;
        if (info.id) aidRef.current = info.id;
        const eligible = info.rideshare_status === "approved" && !!info.rideshare_online;
        if (eligible && !activeRef.current) {
          activeRef.current = true;
          pollTrips();
          intervalId = setInterval(pollTrips, POLL_MS);
        }
        if (!eligible && activeRef.current) {
          activeRef.current = false;
          if (intervalId) { clearInterval(intervalId); intervalId = null; }
        }
      } catch (_) { /* noop */ }
    };
    checkAndStart();
    const gateId = setInterval(checkAndStart, 20000);
    return () => {
      cancelled = true;
      clearInterval(gateId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [token, user?.role, pollTrips, demo]);

  // While a request is on screen, watch it: cancelled / taken / timed out → say so and move on.
  useEffect(() => {
    if (!incomingId || !token || demo) return undefined;
    setGone(null);
    let stop = false;
    const check = async () => {
      try {
        const { data: b } = await axios.get(`${API}/rideshare/bookings/${incomingId}?token=${token}`);
        if (stop || !b) return;
        const mine = b.artisan_user_id === user?.id;
        const o = current?.offer ? (b.offers || []).find((x) => x?.id === current.offer.id) : null;
        // Already handled from the trip page (accepted / countered there): just close quietly.
        if ((mine && ["accepted", "in_progress", "completed"].includes(b.status)) || (o && (o.by === "driver" || o.status === "accepted"))) {
          // Customer accepted this pro's counter → confirmation slide-up with "Go to ride / job".
          if (mine && b.status === "accepted") window.dispatchEvent(new CustomEvent("yorm:job-accepted", { detail: { booking: b } }));
          dismiss(); return;
        }
        const reason = b.status === "cancelled" ? "The customer cancelled this request."
          : ["expired", "declined"].includes(b.status) ? "This request has expired."
          : !["pending", "negotiating", "searching"].includes(b.status) && b.artisan_user_id && !mine
            ? "Another driver took this request."
          : o && o.status === "superseded" ? "Another driver took this request."
          : o && o.status === "expired" ? "Time's up — this request has moved on."
          : o && o.status !== "open" ? "This offer is closed." : null;
        if (reason) setGone(reason);
      } catch (e) {
        if (!stop && e?.response?.status === 404) setGone("This request is no longer available.");
      }
    };
    const iv = setInterval(check, 3000);
    const onUpd = (ev) => { if (!ev?.detail?.booking_id || ev.detail.booking_id === incomingId) check(); };
    window.addEventListener("yorm:booking-updated", onUpd);
    return () => { stop = true; clearInterval(iv); window.removeEventListener("yorm:booking-updated", onUpd); };
  }, [incomingId, incomingSig, token, user?.id, demo]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!gone) return undefined;
    const t = setTimeout(() => dismiss(), 4000);
    return () => clearTimeout(t);
  }, [gone]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismiss = () => {
    if (voiceAudioRef.current) {
      try { voiceAudioRef.current.pause(); } catch { /* noop */ }
      voiceAudioRef.current = null;
    }
    setVoicePlaying(false);
    setGone(null);
    setCounterOpen(false);
    setCounterAmount("");
    setQueue((prev) => prev.slice(1).filter((it) => windowLive(tripWindow(it.trip, it.offer))));
  };

  // Auto-play the runner / van voice note when the request appears.
  useEffect(() => {
    if (!incoming) return;
    const van = incoming.rideshare_type === "ride" && !!incoming.van_voice_note;
    const url = van ? incoming.van_voice_note : incoming.runner_voice_note;
    if (!url || (!van && incoming.rideshare_type !== "runner") || voiceAudioRef.current) return;
    const audio = new Audio(resolveMediaUrl(url, API));
    audio.addEventListener("ended", () => setVoicePlaying(false));
    voiceAudioRef.current = audio;
    audio.play().then(() => setVoicePlaying(true)).catch(() => setVoicePlaying(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incoming?.id]);

  const toggleVoiceNote = () => {
    const url = incoming?.van_voice_note || incoming?.runner_voice_note;
    if (!url) return;
    if (!voiceAudioRef.current) {
      voiceAudioRef.current = new Audio(resolveMediaUrl(url, API));
      voiceAudioRef.current.addEventListener("ended", () => setVoicePlaying(false));
    }
    if (voicePlaying) {
      voiceAudioRef.current.pause();
      setVoicePlaying(false);
    } else {
      voiceAudioRef.current.currentTime = 0;
      voiceAudioRef.current.play().then(() => setVoicePlaying(true)).catch(() => setVoicePlaying(false));
    }
  };

  const acceptNow = async () => {
    if (!incoming) return;
    const offer = current.offer;
    setBusy(true);
    try {
      let accepted = null;
      if (offer) {
        const r = await axios.post(`${API}/rideshare/bookings/${incoming.id}/offer?token=${token}`, { action: "accept", target_offer_id: offer.id });
        accepted = r?.data?.booking || r?.data;
      } else {
        const r = await axios.put(`${API}/rideshare/bookings/${incoming.id}/status?token=${token}`, { status: "accepted" });
        accepted = r?.data?.booking || r?.data;
      }
      notifyBookingUpdated("offer_accepted", incoming.id);
      const id = incoming.id;
      dismiss();
      // Confirmation slide-up with the "Go to ride / job" button (AcceptedJobSheet).
      const booking = { ...incoming, ...(accepted && accepted.id === id ? accepted : {}), id, status: "accepted", artisan_user_id: user?.id };
      window.dispatchEvent(new CustomEvent("yorm:job-accepted", { detail: { booking } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Accept failed");
    } finally {
      setBusy(false);
    }
  };

  const declineNow = async () => {
    if (!incoming) return;
    setBusy(true);
    try {
      const offer = current.offer;
      let sent = true;
      if (incoming.status === "pending" && incoming.artisan_user_id === user?.id) {
        await axios.put(`${API}/rideshare/bookings/${incoming.id}/status?token=${token}`, { status: "declined" });
      } else if (offer && (offer.driver_id === aidRef.current || incoming.artisan_user_id === user?.id)) {
        await axios.post(`${API}/rideshare/bookings/${incoming.id}/offer?token=${token}`, { action: "decline", target_offer_id: offer.id });
      } else {
        sent = false; // broadcast request with no thread for me: just step aside
      }
      toast.success(sent ? "Request declined" : "Request dismissed");
      if (sent) notifyBookingUpdated("offer_declined", incoming.id);
      dismiss();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Decline failed");
      dismiss();
    } finally {
      setBusy(false);
    }
  };

  const sendCounter = async () => {
    if (!incoming) return;
    const amt = parseFloat(counterAmount);
    if (!amt || amt <= 0) { toast.error("Enter a positive GHS amount"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/rideshare/bookings/${incoming.id}/offer?token=${token}`, { action: "counter", amount: amt });
      toast.success(`Counter offer of GHS ${amt.toFixed(2)} sent`);
      notifyBookingUpdated("offer_countered", incoming.id);
      dismiss();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Counter failed");
    } finally {
      setBusy(false);
    }
  };

  if (!incoming) return null;

  const offered = tripFare(incoming);
  const fare = offered != null ? offered.toFixed(2) : null;
  const countered = isRiderOffer(incoming);
  const counterBack = countered && Number(current.offer?.counter_count || 0) > 0;
  const km = incoming.estimated_distance_km != null ? Number(incoming.estimated_distance_km).toFixed(1) : null;
  const isRunner = incoming.rideshare_type === "runner";
  const van = isVanTrip(incoming);
  const vanWindowLabel = (() => {
    if (!van || !incoming.van_scheduled_start) return null;
    try {
      const start = new Date(incoming.van_scheduled_start);
      const opts = { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" };
      let out = start.toLocaleString(undefined, opts);
      if (incoming.van_scheduled_end) {
        const end = new Date(incoming.van_scheduled_end);
        const sameDay = start.toDateString() === end.toDateString();
        out += ` – ${sameDay ? end.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : end.toLocaleString(undefined, opts)}`;
      }
      return out;
    } catch (_) { return null; }
  })();
  const typeMeta = ({
    ride:     { icon: Car,     title: "New ride request" },
    motor:    { icon: Bike,    title: "New motor request" },
    delivery: { icon: Package, title: "New delivery request" },
    runner:   { icon: User,    title: "New runner request" },
  }[incoming.rideshare_type]) || { icon: Car, title: "New trip request" };
  const flavorMeta = incoming.service_flavor === "trash"
    ? { icon: Trash2, title: `Trash Pickup (${incoming.trash_size || "small"}) — collect payment on arrival` }
    : incoming.service_flavor === "laundry"
    ? { icon: WashingMachine, title: incoming.laundry_direction === "dropoff" ? "Laundry Pick Up — paid on delivery" : "Laundry Drop Off — collect payment before you leave" }
    : typeMeta;
  const typeMetaFinal = counterBack ? { icon: flavorMeta.icon, title: "Customer countered your offer" } : flavorMeta;

  return (
    <PersistentSheet open title={typeMetaFinal.title} icon={typeMetaFinal.icon} testId="incoming-trip-modal">
        {gone && (
          <div className="rounded-2xl bg-red-50 border border-red-200 px-4 py-3 text-[15px] font-semibold text-red-700 text-center" data-testid="incoming-trip-gone">
            {gone}
          </div>
        )}

        <div className="space-y-3">
          <div className="rounded-2xl bg-gradient-to-br from-[#4F988E] to-[#3d7a72] text-white p-4 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wider text-white" data-testid="incoming-trip-fare-label">{countered ? "Customer's offer" : "Estimated fare"}</p>
              <p className="text-3xl font-bold text-white" data-testid="incoming-trip-fare">
                {fare ? `GHS ${fare}` : "—"}
              </p>
              {countered && (
                <p className="text-xs text-white/90 line-through" data-testid="incoming-trip-estimate">
                  {counterBack && current.offer?.previous_amount != null ? `Your offer GHS ${Number(current.offer.previous_amount).toFixed(2)}` : `Estimate GHS ${tripEstimate(incoming).toFixed(2)}`}
                </p>
              )}
              <p className="text-xs text-white mt-1">
                {km ? `${km} km` : ""}
                {incoming.rideshare_type ? ` · ${incoming.rideshare_type}` : ""}
              </p>
            </div>
            <div className="shrink-0 bg-white/95 rounded-xl p-2 shadow-inner">
              <CircularCountdown
                startedAt={win.startedAt}
                totalSec={win.totalSec}
                size={72}
                stroke={6}
                testId="incoming-trip-countdown"
                onExpire={() => setGone("Time's up — this request has moved on.")}
              />
            </div>
          </div>

          {van && (
            <div className="rounded-2xl border border-indigo-500/30 bg-indigo-500/5 p-3 space-y-2" data-testid="incoming-trip-van-block">
              {vanWindowLabel && (
                <div className="flex items-center gap-2 rounded-xl bg-white border border-indigo-400/40 px-3 py-2" data-testid="incoming-trip-van-window">
                  <CalendarClock size={16} className="text-indigo-600 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold">Scheduled window</p>
                    <p className="text-sm font-medium text-gray-800 truncate">{vanWindowLabel}</p>
                  </div>
                </div>
              )}
              {incoming.van_voice_note && (
                <button type="button" onClick={toggleVoiceNote}
                  className="w-full flex items-center gap-3 rounded-full bg-white border border-indigo-500/40 px-3 py-2 hover:bg-indigo-500/10 transition"
                  data-testid="incoming-trip-van-voice-play" aria-label={voicePlaying ? "Pause voice note" : "Play voice note"}>
                  <span className="w-9 h-9 rounded-full bg-indigo-600 text-white flex items-center justify-center shrink-0">
                    {voicePlaying ? <Pause size={16} /> : <Play size={16} />}
                  </span>
                  <span className="flex-1 text-left">
                    <span className="block text-xs font-semibold text-indigo-700">Van job brief</span>
                    <span className="block text-[11px] text-gray-500">{voicePlaying ? "Playing… tap to pause" : "Tap to replay"}</span>
                  </span>
                  <Mic size={16} className="text-indigo-500 shrink-0" />
                </button>
              )}
              {incoming.van_note && (
                <p className="text-sm text-gray-700 leading-snug" data-testid="incoming-trip-van-note">{incoming.van_note}</p>
              )}
            </div>
          )}

          <RequestMedia media={incoming.request_media} testId="incoming-trip-media" />
          {incoming.laundry_receipt_photo && (
            <RequestMedia media={[incoming.laundry_receipt_photo]} testId="incoming-trip-laundry-receipt" title="Laundry receipt · show it at the laundry to collect the clothes" />
          )}
          {isRunner && (incoming.runner_voice_note || incoming.runner_description || incoming.runner_video || (Array.isArray(incoming.runner_photos) && incoming.runner_photos.length > 0)) && (
            <div className="rounded-2xl border border-[#8B5CF6]/30 bg-[#8B5CF6]/5 p-3 space-y-2" data-testid="incoming-trip-runner-block">
              {incoming.runner_voice_note && (
                <button type="button" onClick={toggleVoiceNote}
                  className="w-full flex items-center gap-3 rounded-full bg-white border border-[#8B5CF6]/40 px-3 py-2 hover:bg-[#8B5CF6]/10 transition"
                  data-testid="incoming-trip-voice-play" aria-label={voicePlaying ? "Pause voice note" : "Play voice note"}>
                  <span className="w-9 h-9 rounded-full bg-[#8B5CF6] text-white flex items-center justify-center shrink-0">
                    {voicePlaying ? <Pause size={16} /> : <Play size={16} />}
                  </span>
                  <span className="flex-1 text-left">
                    <span className="block text-xs font-semibold text-[#7C3AED]">Customer voice note</span>
                    <span className="block text-[11px] text-gray-500">{voicePlaying ? "Playing… tap to pause" : "Tap to replay"}</span>
                  </span>
                  <Mic size={16} className="text-[#8B5CF6] shrink-0" />
                </button>
              )}
              {incoming.runner_description && (
                <p className="text-sm text-gray-700 leading-snug" data-testid="incoming-trip-runner-desc">{incoming.runner_description}</p>
              )}
              {Array.isArray(incoming.runner_photos) && incoming.runner_photos.length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="incoming-trip-runner-photos">
                  {incoming.runner_photos.map((url, i) => (
                    <a key={i} href={resolveMediaUrl(url, API)} target="_blank" rel="noopener noreferrer" className="block">
                      <img src={resolveMediaUrl(url, API)} alt={`Customer reference ${i + 1}`} loading="lazy" decoding="async"
                        className="w-16 h-16 rounded-md object-cover border border-[#8B5CF6]/30 hover:opacity-90 transition" />
                    </a>
                  ))}
                </div>
              )}
              {incoming.runner_video && (
                <video src={resolveMediaUrl(incoming.runner_video, API)} controls playsInline preload="metadata"
                  className="w-full max-h-48 rounded-md bg-black" data-testid="incoming-trip-runner-video" />
              )}
            </div>
          )}

          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-2">
              <MapPin size={16} className="text-green-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-500">Pickup</p>
                <p className="text-gray-800">{incoming.pickup_address || "—"}</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <MapPin size={16} className="text-red-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-500">Drop-off</p>
                <p className="text-gray-800">{incoming.dropoff_address || "—"}</p>
              </div>
            </div>
          </div>

          {gone ? (
            <Button variant="outline" onClick={dismiss} className="w-full rounded-full" data-testid="incoming-trip-close">Close</Button>
          ) : counterOpen ? (
            <div className="border-t border-gray-100 pt-3 space-y-2" data-testid="incoming-trip-counter-form">
              <label className="text-xs font-semibold text-gray-700">Your counter offer (GHS)</label>
              <div className="flex gap-2">
                <Input type="number" min="1" step="0.5" value={counterAmount} onChange={(e) => setCounterAmount(e.target.value)}
                  placeholder={fare || "0.00"} className="flex-1" data-testid="incoming-trip-counter-input" autoFocus />
                <Button onClick={sendCounter} disabled={busy} className="bg-[#4F988E] hover:bg-[#3d7a72] text-white" data-testid="incoming-trip-counter-send">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : "Send"}
                </Button>
              </div>
              <button type="button" onClick={() => { setCounterOpen(false); setCounterAmount(""); }}
                className="text-xs text-gray-500 hover:text-gray-700" data-testid="incoming-trip-counter-back">
                Back
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 pt-2">
              <Button variant="outline" onClick={() => { setCounterOpen(true); setCounterAmount(fare || ""); }} disabled={busy}
                className="rounded-full" data-testid="incoming-trip-counter">
                <MessageSquare size={16} className="mr-1" />
                Counter offer
              </Button>
              <Button className="bg-green-600 hover:bg-green-700 text-white rounded-full" onClick={acceptNow} disabled={busy} data-testid="incoming-trip-accept">
                {busy ? <Loader2 size={16} className="animate-spin" /> : (<><Check size={16} className="mr-1" />{acceptLabel(incoming)}</>)}
              </Button>
              <Button variant="outline" onClick={declineNow} disabled={busy}
                className="col-span-2 rounded-full border-red-200 text-red-600 hover:bg-red-50" data-testid="incoming-trip-decline">
                <X size={16} className="mr-1" />
                Decline
              </Button>
              {queue.length > 1 && (
                <p className="col-span-2 text-center text-xs text-gray-500" data-testid="incoming-trip-queue-count">
                  {queue.length - 1} more request{queue.length > 2 ? "s" : ""} waiting
                </p>
              )}
            </div>
          )}
        </div>
    </PersistentSheet>
  );
}
